/**
 * Organizations Page (Phase 30 Wave 4 chunk 30.4.3b)
 *
 * Super-admin-only master list of customer organizations + Create flow.
 * Sits under Settings → Platform alongside Camera Registry (chunk 30.4.2).
 *
 * Access:
 * - Backend routes (`/api/admin/orgs/*`) are super_admin-gated via
 *   `requireSuperAdmin` middleware (see chunks 30.4.0 + 30.4.3a).
 * - Sidebar nav entry is hidden below level 6 by DashboardLayout filtering
 *   (chunk 30.4.1).
 * - This page additionally guards client-side: a manager/admin who types
 *   the URL is redirected to /dashboard.
 *
 * Chunk 30.4.3b ships the master list + Create modal only. Each row click
 * toggles an inline expansion that currently shows placeholder copy; the
 * detail panel + Add User + per-org toggle clusters land in chunk 30.4.3c.
 */

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Building2,
  Plus,
  AlertCircle,
  Loader2,
  CheckCircle2,
  CircleSlash,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useRoleLevel } from '../../../lib/hooks/useRoleLevel';
import { useCurrentUser } from '../../../hooks/useUsers';
import { useDocumentTitle } from '../../../lib/hooks/useDocumentTitle';
import {
  useOrganizations,
  type OrganizationListItem,
} from '../../../hooks/useOrganizations';
import { CreateOrganizationModal } from '../../../components/admin/orgs/CreateOrganizationModal';
import { OrganizationDetailPanel } from '../../../components/admin/orgs/OrganizationDetailPanel';

export function OrganizationsPage() {
  useDocumentTitle('Organizations');

  // While /api/me is in flight, level === 0; render a hold state instead
  // of either the cross-org query (which requires super_admin) or the
  // redirect (which would race the loading state on first paint).
  const { isLoading: meLoading } = useCurrentUser();
  const { level } = useRoleLevel();

  if (meLoading) {
    return null;
  }

  if (level < 6) {
    return <Navigate to="/dashboard" replace />;
  }

  return <OrganizationsPageInner />;
}

function OrganizationsPageInner() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [expandedOrgId, setExpandedOrgId] = useState<string | null>(null);

  const { data: organizations, isLoading, error, refetch } = useOrganizations();

  const handleOpenCreate = () => setIsCreateOpen(true);
  const handleCloseCreate = () => setIsCreateOpen(false);

  const toggleExpand = (orgId: string) => {
    setExpandedOrgId((current) => (current === orgId ? null : orgId));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-guild-mint/50">
            <Building2 size={24} className="text-guild-green" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Organizations
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Customer tenants on the platform — create new orgs and manage their settings
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleOpenCreate}
          className="
            inline-flex items-center gap-2 rounded-xl
            bg-guild-green px-5 py-2.5
            text-sm font-semibold text-white
            shadow-lg shadow-guild-green/20
            hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
            active:scale-[0.98]
            transition-all duration-200
          "
        >
          <Plus size={18} strokeWidth={2.5} />
          Create Organization
        </button>
      </div>

      {/* Body */}
      {error ? (
        <ErrorState onRetry={() => refetch()} />
      ) : isLoading ? (
        <LoadingState />
      ) : organizations && organizations.length > 0 ? (
        <OrganizationTable
          organizations={organizations}
          expandedOrgId={expandedOrgId}
          onToggleExpand={toggleExpand}
        />
      ) : (
        <EmptyState onCreate={handleOpenCreate} />
      )}

      {/* Create Modal */}
      <CreateOrganizationModal
        isOpen={isCreateOpen}
        onClose={handleCloseCreate}
      />
    </div>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

interface OrganizationTableProps {
  organizations: OrganizationListItem[];
  expandedOrgId: string | null;
  onToggleExpand: (orgId: string) => void;
}

function OrganizationTable({
  organizations,
  expandedOrgId,
  onToggleExpand,
}: OrganizationTableProps) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/50">
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-8" />
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Name
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Slug
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Users
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Training Distribution
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Living SOPs
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Created
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {organizations.map((org) => (
            <OrganizationRow
              key={org.id}
              org={org}
              isExpanded={expandedOrgId === org.id}
              onToggleExpand={() => onToggleExpand(org.id)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface OrganizationRowProps {
  org: OrganizationListItem;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

function OrganizationRow({ org, isExpanded, onToggleExpand }: OrganizationRowProps) {
  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <>
      <tr
        onClick={onToggleExpand}
        className={`
          group cursor-pointer transition-colors
          ${isExpanded ? 'bg-gray-50/70' : 'hover:bg-gray-50/50'}
        `}
        aria-expanded={isExpanded}
      >
        {/* Expander chevron */}
        <td className="px-6 py-4">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
            className="
              flex h-7 w-7 items-center justify-center rounded-lg
              text-gray-400 hover:bg-gray-100 hover:text-gray-600
              transition-colors
            "
          >
            {isExpanded ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronRight size={16} />
            )}
          </button>
        </td>

        {/* Name */}
        <td className="px-6 py-4">
          <p className="text-sm font-semibold text-gray-900">{org.name}</p>
          {org.auth0OrgId && (
            <p className="text-xs text-gray-400 font-mono mt-0.5 truncate max-w-[14rem]">
              {org.auth0OrgId}
            </p>
          )}
        </td>

        {/* Slug */}
        <td className="px-6 py-4">
          <code className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
            {org.slug}
          </code>
        </td>

        {/* Users count */}
        <td className="px-6 py-4">
          <span className="text-sm font-medium text-gray-900">{org.userCount}</span>
          <span className="ml-1 text-xs text-gray-500">
            {org.userCount === 1 ? 'user' : 'users'}
          </span>
        </td>

        {/* Training Distribution */}
        <td className="px-6 py-4">
          <ToggleStateChip enabled={org.trainingDistributionEnabled} />
        </td>

        {/* Living SOPs */}
        <td className="px-6 py-4">
          <ToggleStateChip enabled={org.livingSopsEnabled} />
        </td>

        {/* Created */}
        <td className="px-6 py-4">
          <span className="text-sm text-gray-600">{formatDate(org.createdAt)}</span>
        </td>
      </tr>

      {isExpanded && (
        <tr className="bg-gray-50/40">
          <td colSpan={7} className="px-6 py-6 border-t border-gray-100">
            <OrganizationDetailPanel orgId={org.id} />
          </td>
        </tr>
      )}
    </>
  );
}

function ToggleStateChip({ enabled }: { enabled: boolean }) {
  if (enabled) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold bg-emerald-50 text-emerald-700">
        <CheckCircle2 size={12} />
        Enabled
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold bg-gray-100 text-gray-500">
      <CircleSlash size={12} />
      Disabled
    </span>
  );
}

function LoadingState() {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 size={32} className="text-guild-green animate-spin" />
        <p className="mt-4 text-sm text-gray-500">Loading organizations...</p>
      </div>
    </div>
  );
}

interface ErrorStateProps {
  onRetry: () => void;
}

function ErrorState({ onRetry }: ErrorStateProps) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
      <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm font-medium text-red-800">
          Failed to load organizations
        </p>
        <p className="text-sm text-red-600">
          Please try again or contact support if the issue persists.
        </p>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="
          text-sm font-semibold text-red-700 hover:text-red-800
          whitespace-nowrap px-3 py-1.5 rounded-lg hover:bg-red-100
          transition-colors
        "
      >
        Retry
      </button>
    </div>
  );
}

interface EmptyStateProps {
  onCreate: () => void;
}

function EmptyState({ onCreate }: EmptyStateProps) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-guild-mint/40 mb-5">
          <Building2 size={32} className="text-guild-green" />
        </div>

        <h3 className="text-lg font-semibold text-gray-900">
          No organizations yet
        </h3>

        <p className="mt-2 text-sm text-gray-500 max-w-sm">
          The platform has no customer tenants. Create the first organization to bootstrap.
        </p>

        <button
          type="button"
          onClick={onCreate}
          className="
            mt-6 inline-flex items-center gap-2 rounded-xl
            bg-guild-green px-5 py-2.5
            text-sm font-semibold text-white
            shadow-lg shadow-guild-green/20
            hover:bg-guild-green/95
            transition-all duration-200
          "
        >
          <Plus size={18} strokeWidth={2.5} />
          Create your first organization
        </button>
      </div>
    </div>
  );
}

export default OrganizationsPage;
